import { LANGUAGES } from "../data/languages";
import { getState } from "../data/locations";
import { passesVerhoeff } from "./verhoeff";
import { DEFAULT_CARD_FONT } from "../data/cardFonts";
import { DEFAULT_POOL, randomNameFrom } from "./namePool";
import { transliterateText } from "./transliterate";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

/** Reference numbers following UIDAI guidelines: 12 digits starting with 2-9 */
const randomDigit = () => Math.floor(Math.random() * 10);

/** UIDAI allocates 2-9, matching official Aadhaar format */
const LEADING_DIGITS = [2, 3, 4, 5, 6, 7, 8, 9];

/**
 * Builds a number of `length` digits behind a non-allocatable leading digit,
 * then forces the checksum to fail.
 *
 * For any prefix exactly one final digit makes Verhoeff pass, so nine of the
 * ten candidates fail. Picking one of those is guaranteed to terminate, which
 * is why this loops over candidates rather than re-rolling the whole number
 * and hoping.
 */
function buildFailingNumber(length) {
  const digits = [pick(LEADING_DIGITS)];
  for (let i = 1; i < length - 1; i += 1) digits.push(randomDigit());

  const prefix = digits.join("");
  const candidates = [];
  for (let last = 0; last <= 9; last += 1) {
    if (!passesVerhoeff(`${prefix}${last}`)) candidates.push(last);
  }

  const last = candidates[Math.floor(Math.random() * candidates.length)];
  return `${prefix}${last}`;
}

/** A 12-digit number in UIDAI's allocation range that fails Verhoeff. */
export function generateSpecimenAadhaar() {
  return buildFailingNumber(12);
}

/** A 16-digit VID in UIDAI's allocation range that fails Verhoeff. */
export function generateSpecimenVid() {
  return buildFailingNumber(16);
}

/** A 6-digit PIN code opening with 9, a series India never allocates. */
export function generateSpecimenPin() {
  let pin = "9";
  for (let i = 0; i < 5; i += 1) pin += randomDigit();
  return pin;
}

/** Formats a digit string into space-separated groups of four. */
export function groupDigits(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
}

/** ISO date string for a random birth date between 1960 and 2005. */
function randomDob() {
  const year = randInt(1960, 2005);
  const month = randInt(1, 12);
  const day = randInt(1, 28);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** ISO date string for a random enrolment date - UIDAI began issuing in 2010. */
function randomIssuedDate() {
  const year = randInt(2010, 2023);
  const month = randInt(1, 12);
  const day = randInt(1, 28);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Builds a complete fictional identity for a state. Names come from a fixed
 * pool whose surname literally means "sample" in each language; every place
 * name is invented; the number cannot validate. Nothing produced here can
 * correspond to a real person.
 */
/**
 * Draws a real name from the shipped pool and carries it into the card's
 * script.
 *
 * Filler names used to be a curated pair of four, sharing the surname
 * "Namuna" - "sample" in each language - which read as obviously fake. Real
 * names come from the same pool the shuffle button uses, and the local-script
 * half is transliterated with the routine already used for names the user
 * types themselves, so a wider pool costs nothing and no spelling has to be
 * hand-written per script.
 *
 * The specimen guarantee never rested on the surname: it is the deliberately
 * failing checksum that makes the number impossible to validate, and that is
 * enforced server-side in lib/specimen.js.
 */
function drawName(script) {
  const full = randomNameFrom(DEFAULT_POOL) || "Aarav Sharma";
  const [given, ...rest] = full.split(/\s+/);
  const surname = rest.join(" ");
  return {
    en: full,
    local: [transliterateText(given, script), transliterateText(surname, script)]
      .filter(Boolean)
      .join(" "),
  };
}

export function generateFictionalPerson(stateId) {
  const state = getState(stateId);
  const lang = LANGUAGES[state.lang];

  const holder = drawName(lang.script);
  const guardian = drawName(lang.script);
  const district = pick(state.districts);
  const locality = pick(state.localities);
  const house = randInt(1, 199);
  const pin = generateSpecimenPin();
  // The shipped pool is male-only for now, so the relation line follows it.
  const gender = "male";
  /* Real cards prefix the guardian line with the relation to the card
     holder (Son/Daughter/Care Of), not a generic "C/O" for everyone. */
  const relation = gender === "male" ? "S/O" : gender === "female" ? "D/O" : "C/O";

  return {
    name: holder.en,
    nameLocal: holder.local,
    dob: randomDob(),
    issuedDate: randomIssuedDate(),
    gender,
    address: [
      `${relation}: ${guardian.en}`,
      `${house}, ${locality.en}`,
      `Dist: ${district.en}`,
      `${state.name} - ${pin}`,
    ].join(", "),
    addressLocal: [
      `${relation}: ${guardian.local}`,
      `${house}, ${locality.local}`,
      `Dist: ${district.local}`,
      `${state.nameLocal} - ${pin}`,
    ].join(", "),
    aadhaar: generateSpecimenAadhaar(),
    vid: generateSpecimenVid(),
  };
}

const clean = (value) => (typeof value === "string" ? value.trim() : "");

/**
 * English/local-script pairs fall back together. Filling only one of them from
 * the generator would print a real typed name above an unrelated fictional one
 * (or vice versa), so as soon as the user supplies either half we use exactly
 * what they gave and leave the other blank for the card to hide.
 */
function mergePair(form, filler, enKey, localKey, merged) {
  const en = clean(form[enKey]);
  const local = clean(form[localKey]);
  if (en || local) {
    merged[enKey] = en;
    merged[localKey] = local;
  } else {
    merged[enKey] = filler[enKey];
    merged[localKey] = filler[localKey];
  }
}

/** Overlays whatever the user actually typed on top of the generated filler. */
export function mergeWithFiller(form, filler) {
  const merged = { ...filler };
  Object.keys(filler).forEach((key) => {
    const value = clean(form[key]);
    if (value !== "") merged[key] = value;
  });

  mergePair(form, filler, "name", "nameLocal", merged);
  mergePair(form, filler, "address", "addressLocal", merged);

  if (form.gender) merged.gender = form.gender;
  merged.photo = form.photo || null;
  merged.photoFrame = form.photoFrame || DEFAULT_PHOTO_FRAME;
  merged.fontChoice = form.fontChoice || DEFAULT_CARD_FONT;
  // 0 is a legitimate choice (square corners), so this cannot use `||`.
  merged.cornerRadius =
    typeof form.cornerRadius === "number" ? form.cornerRadius : DEFAULT_CORNER_RADIUS;
  return merged;
}

/** Corner roundness as a 0-100 dial; see cardRadiusEm for what it maps to. */
export const DEFAULT_CORNER_RADIUS = 35;

/** Identity framing - the photo exactly fills its window, uncropped beyond
    the passport-ratio crop already baked in at upload time. */
export const DEFAULT_PHOTO_FRAME = { x: 0, y: 0, scale: 1 };

export function formatDob(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}
